import axios from 'axios';
import { resolveApiUrl } from '../utils/api';

// Multi-Agent System Types
interface MultiAgentRequest {
  workzone: Array<{ lat: number; lng: number }>;
  setup_id: string;
  state?: string;
  work_type?: string;
}

interface MultiAgentResponse {
  traffic_plan: {
    signs: Array<{
      sign_type: string;
      lat: number;
      lng: number;
      distance_upstream_m: number;
      side: string;
      orientation: string;
      message: string;
    }>;
    cones: {
      coordinates: Array<{ lat: number; lng: number }>;
      spacing_m: number;
      taper_type: string;
    };
    setup_compliance: {
      compliant: boolean;
      warnings: string[];
      notes: string[];
    };
  };
  road_details: {
    road_name: string;
    speed_limit_kph: number;
    lane_count: number;
    road_type: string;
    is_one_way: boolean;
    is_divided: boolean;
    primary_road_geometry: Array<{ lat: number; lng: number }>;
    side_streets: Array<{
      name: string;
      entry_point: {
        lat: number;
        lng: number;
      };
      intersection_type: string;
    }>;
    potential_advance_warning_points: Array<{
      lat: number;
      lng: number;
      distance_upstream_m: number;
    }>;
  };
}

// Multi-Agent System API
export const generateAgentPlan = async (
  workzone: Array<{ lat: number; lng: number }>,
  setupId: string,
  state: string = 'SA',
  workType: string = 'maintenance'
): Promise<MultiAgentResponse> => {
  const payload: MultiAgentRequest = {
    workzone,
    setup_id: setupId,
    state,
    work_type: workType
  };
  
  const response = await axios.post(resolveApiUrl('/api/agent/generate'), payload, {
    headers: {
      'Content-Type': 'application/json'
    },
    timeout: 90000 // 90 seconds for AI processing
  });
  
  return response.data;
};

// Legacy OSM Analysis API (for fallback)
export const getCombinedAnalysis = async (
  polygon: Array<{ lat: number; lng: number }>,
  setupId: string,
  focus?: any
): Promise<any> => {
  const payload = { 
    workzone: { polygon }, 
    setup_id: setupId,
    focus: focus || { jurisdiction: 'SA', mode: 'radius' } 
  };
  const response = await axios.post(resolveApiUrl('/api/osm/analyze'), payload, {
    headers: {
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

export const getSignLibrary = async (): Promise<any> => {
  const response = await axios.get(resolveApiUrl('/api/signs/library'));
  return response.data;
};

export const exportPlan = async (
  signs: any[],
  printAreaBounds: any,
  zoom: number,
  format: 'pdf' | 'png' | 'jpeg' = 'pdf',
  titleBoxData?: any,
  highQuality: boolean = false,
  workzone?: any,
  arrows?: any[],
  tilt?: number,
  heading?: number,
  signBaseSize?: number,
  compositeBaseSize?: number,
  paperWidth?: number,
  paperHeight?: number,
  mapProvider?: 'google' | 'mapbox',
  capturedMapImage?: string | null,  // Base64 data URL of captured canvas (Mapbox only)
  mapFadeOpacity?: number,
  capturedAnnotatedCanvas?: string | null  // Base64 data URL of full page with annotations (from annotation editor)
): Promise<Blob> => {
  const payload = {
    signs,
    printAreaBounds,
    zoom,
    titleBoxData,
    highQuality,
    workzone,
    arrows,
    tilt,
    heading,
    signBaseSize,
    compositeBaseSize,
    paperWidth,
    paperHeight,
    mapProvider: mapProvider || 'google',
    capturedMapImage,  // Send captured canvas for Mapbox WYSIWYG export
    mapFadeOpacity,
    capturedAnnotatedCanvas,  // Send full annotated page for WYSIWYG export
  };
  const response = await axios.post(resolveApiUrl(`/api/plan/export/${format}`), payload, {
    responseType: 'blob',
    withCredentials: true,
    validateStatus: () => true,
  });
  const contentType = (response.headers['content-type'] || '').toLowerCase();
  const isOk = !!response.status && response.status < 400;
  const isSupportedType =
    contentType.startsWith('application/pdf') ||
    contentType.startsWith('image/png') ||
    contentType.startsWith('image/jpeg');
  if (!isOk || !isSupportedType) {
    throw new Error(`Export failed (${response.status})`);
  }
  return response.data as Blob;
}; 